import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Spinner, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { readUsersMeUsersMeGetOptions } from '@/client/@tanstack/react-query.gen';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader';
import { Routes } from '@/routes';
import { hasProActiveSubscription } from '@/utils/userUtils';

const PRO_PAYMENT_JUST_CONFIRMED_KEY = 'proPaymentJustConfirmed';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 10;
const REDIRECT_DELAY_MS = 1500;

/**
 * Handles the post-subscription redirect from Stripe Checkout.
 * Polls the user's subscription status until the Pro subscription is active,
 * avoiding the webhook delivery race condition where the subscription appears
 * unpaid immediately after a successful Stripe Checkout redirect.
 */
function ProPaymentSuccessPage() {
	const navigate = useNavigate();

	const params = new URLSearchParams(window.location.search);
	const hasStripeParams = !!params.get('session_id');

	if (hasStripeParams && sessionStorage.getItem(PRO_PAYMENT_JUST_CONFIRMED_KEY) !== 'true') {
		sessionStorage.setItem(PRO_PAYMENT_JUST_CONFIRMED_KEY, 'true');
	}

	const paymentJustConfirmed = sessionStorage.getItem(PRO_PAYMENT_JUST_CONFIRMED_KEY) === 'true';

	const { data: user } = useQuery({
		...readUsersMeUsersMeGetOptions(),
		staleTime: 0,
		gcTime: 0,
		refetchInterval: (query) => {
			if (!paymentJustConfirmed) return false;
			if (hasProActiveSubscription(query.state.data ?? null)) return false;
			const fetchCount = query.state.fetchFailureCount + (query.state.dataUpdateCount ?? 0);
			if (fetchCount >= MAX_POLL_ATTEMPTS) return false;
			return POLL_INTERVAL_MS;
		},
	});

	const subscriptionConfirmed = !paymentJustConfirmed || hasProActiveSubscription(user ?? null);

	useEffect(() => {
		if (!subscriptionConfirmed) return;
		sessionStorage.removeItem(PRO_PAYMENT_JUST_CONFIRMED_KEY);
		const timer = setTimeout(() => navigate({ to: Routes.PROPERTIES }), REDIRECT_DELAY_MS);
		return () => clearTimeout(timer);
	}, [subscriptionConfirmed, navigate]);

	const isPolling = paymentJustConfirmed && !hasProActiveSubscription(user ?? null);

	return (
		<GlobalOverlayLoader text="Subscription Activated! 🎉">
			<VStack gap={4} textAlign="center">
				<Text fontSize="sm" color="fg.muted">
					{isPolling
						? 'Confirming your subscription, please wait...'
						: 'Your Pro subscription is confirmed. Redirecting to your properties...'}
				</Text>
				<Spinner size="lg" />
			</VStack>
		</GlobalOverlayLoader>
	);
}

export const Route = createFileRoute({
	component: ProPaymentSuccessPage,
});
